import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, TrendingUp, DollarSign, Bell, FileText, Plus, Trash2, Edit,
  LogOut, Loader2, RefreshCw, CheckCircle, XCircle, Clock, PieChart, ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [investors, setInvestors] = useState([]);
  const [news, setNews] = useState([]);
  const [dividends, setDividends] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [requests, setRequests] = useState([]);
  
  const [showInvestorModal, setShowInvestorModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showDividendModal, setShowDividendModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [investorForm, setInvestorForm] = useState({
    user_id: '', password: '', name: '', email: '', phone: '', city: '',
    shares_held: '', amount_invested: '', entry_share_value: '',
    discounted_share_value: '', current_share_value: ''
  });
  const [newsForm, setNewsForm] = useState({ title: '', content: '', image_url: '', category: 'announcement' });
  const [dividendForm, setDividendForm] = useState({ amount_per_share: '', record_date: '', payment_date: '', description: '' });
  const [documentForm, setDocumentForm] = useState({ title: '', description: '', file_url: '', document_type: 'report' });
  const [newPrice, setNewPrice] = useState('');

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    if (role !== 'admin') {
      navigate('/investors');
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, investorsRes, newsRes, dividendsRes, docsRes, requestsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/dashboard-stats`),
        fetch(`${API_URL}/api/admin/investors`),
        fetch(`${API_URL}/api/news`),
        fetch(`${API_URL}/api/dividends`),
        fetch(`${API_URL}/api/documents`),
        fetch(`${API_URL}/api/admin/investment-requests`)
      ]);

      const [statsData, investorsData, newsData, dividendsData, docsData, requestsData] = await Promise.all([
        statsRes.json(), investorsRes.json(), newsRes.json(), dividendsRes.json(), docsRes.json(), requestsRes.json()
      ]);

      if (statsData.success) setStats(statsData.stats);
      if (investorsData.success) setInvestors(investorsData.investors);
      if (newsData.success) setNews(newsData.news);
      if (dividendsData.success) setDividends(dividendsData.dividends);
      if (docsData.success) setDocuments(docsData.documents);
      if (requestsData.success) setRequests(requestsData.requests);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('userRole');
    navigate('/investors');
  };

  // Create Investor
  const handleCreateInvestor = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...investorForm,
        shares_held: parseInt(investorForm.shares_held),
        amount_invested: parseFloat(investorForm.amount_invested),
        entry_share_value: parseFloat(investorForm.entry_share_value),
        discounted_share_value: parseFloat(investorForm.discounted_share_value),
        current_share_value: parseFloat(investorForm.current_share_value)
      };
      
      const url = editingInvestor 
        ? `${API_URL}/api/admin/investors/${editingInvestor.id}`
        : `${API_URL}/api/admin/investors`;
      
      const response = await fetch(url, {
        method: editingInvestor ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingInvestor ? {
          name: payload.name, email: payload.email, phone: payload.phone, city: payload.city,
          shares_held: payload.shares_held, amount_invested: payload.amount_invested,
          entry_share_value: payload.entry_share_value, discounted_share_value: payload.discounted_share_value,
          current_share_value: payload.current_share_value
        } : payload)
      });
      const data = await response.json();
      if (data.success) {
        setShowInvestorModal(false);
        resetInvestorForm();
        fetchAllData();
      } else {
        alert(data.detail || 'Error saving investor');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const resetInvestorForm = () => {
    setInvestorForm({
      user_id: '', password: '', name: '', email: '', phone: '', city: '',
      shares_held: '', amount_invested: '', entry_share_value: '',
      discounted_share_value: '', current_share_value: ''
    });
    setEditingInvestor(null);
  };

  const handleEditInvestor = (investor) => {
    setEditingInvestor(investor);
    setInvestorForm({
      user_id: investor.user_id, password: '', name: investor.name, email: investor.email || '',
      phone: investor.phone || '', city: investor.city || '',
      shares_held: investor.shares_held?.toString() || '', amount_invested: investor.amount_invested?.toString() || '',
      entry_share_value: investor.entry_share_value?.toString() || '',
      discounted_share_value: investor.discounted_share_value?.toString() || '',
      current_share_value: investor.current_share_value?.toString() || ''
    });
    setShowInvestorModal(true);
  };

  const handleDeleteInvestor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this investor?')) return;
    try {
      await fetch(`${API_URL}/api/admin/investors/${id}`, { method: 'DELETE' });
      fetchAllData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Create News
  const handleCreateNews = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/news`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newsForm)
      });
      const data = await response.json();
      if (data.success) {
        setShowNewsModal(false);
        setNewsForm({ title: '', content: '', image_url: '', category: 'announcement' });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNews = async (id) => {
    if (!window.confirm('Delete this news item?')) return;
    await fetch(`${API_URL}/api/admin/news/${id}`, { method: 'DELETE' });
    fetchAllData();
  };

  // Create Dividend
  const handleCreateDividend = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dividends`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dividendForm, amount_per_share: parseFloat(dividendForm.amount_per_share) })
      });
      const data = await response.json();
      if (data.success) {
        setShowDividendModal(false);
        setDividendForm({ amount_per_share: '', record_date: '', payment_date: '', description: '' });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Create Document
  const handleCreateDocument = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(documentForm)
      });
      const data = await response.json();
      if (data.success) {
        setShowDocumentModal(false);
        setDocumentForm({ title: '', description: '', file_url: '', document_type: 'report' });
        fetchAllData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Update Share Price
  const handleUpdatePrice = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/current-share-price?price=${parseFloat(newPrice)}`, {
        method: 'PUT'
      });
      const data = await response.json();
      if (data.success) {
        setShowPriceModal(false);
        setNewPrice('');
        fetchAllData();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Investment Request
  const handleRequestAction = async (requestId, status) => {
    try {
      await fetch(`${API_URL}/api/admin/investment-requests/${requestId}?status=${status}`, { method: 'PUT' });
      fetchAllData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="text-white">
                <p className="text-white/80 text-sm">Admin Dashboard</p>
                <h1 className="text-xl md:text-2xl font-bold">Investor Management</h1>
              </div>
            </div>
            <div className="flex gap-3">
              <Button onClick={fetchAllData} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button onClick={handleLogout} variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <section className="py-6 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Investors</p>
                    <p className="text-xl font-bold">{stats.total_investors || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Shares</p>
                    <p className="text-xl font-bold">{stats.total_shares?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Invested</p>
                    <p className="text-xl font-bold">₹{stats.total_invested?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Pending Requests</p>
                    <p className="text-xl font-bold">{stats.pending_requests || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setShowPriceModal(true)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Current Share Price</p>
                    <p className="text-xl font-bold">₹{stats.current_share_price || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-6 bg-gray-50 min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="investors" className="space-y-6">
            <TabsList className="flex flex-wrap gap-2">
              <TabsTrigger value="investors">Investors</TabsTrigger>
              <TabsTrigger value="news">News & Updates</TabsTrigger>
              <TabsTrigger value="dividends">Dividends</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="requests">Investment Requests</TabsTrigger>
            </TabsList>

            {/* Investors Tab */}
            <TabsContent value="investors">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Manage Investors</CardTitle>
                  <Button onClick={() => { resetInvestorForm(); setShowInvestorModal(true); }}>
                    <Plus className="w-4 h-4 mr-2" /> Add Investor
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-3 px-2">User ID</th>
                          <th className="py-3 px-2">Name</th>
                          <th className="py-3 px-2">City</th>
                          <th className="py-3 px-2">Shares</th>
                          <th className="py-3 px-2">Invested</th>
                          <th className="py-3 px-2">Current Value</th>
                          <th className="py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {investors.map((inv) => (
                          <tr key={inv.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-2 font-mono">{inv.user_id}</td>
                            <td className="py-3 px-2 font-medium">{inv.name}</td>
                            <td className="py-3 px-2">{inv.city}</td>
                            <td className="py-3 px-2">{inv.shares_held?.toLocaleString()}</td>
                            <td className="py-3 px-2">₹{inv.amount_invested?.toLocaleString()}</td>
                            <td className="py-3 px-2">₹{inv.current_share_value}</td>
                            <td className="py-3 px-2">
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => handleEditInvestor(inv)}>
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteInvestor(inv.id)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {investors.length === 0 && (
                      <p className="text-center py-8 text-gray-500">No investors yet. Add your first investor!</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* News Tab */}
            <TabsContent value="news">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>News & Updates</CardTitle>
                  <Button onClick={() => setShowNewsModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add News
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {news.map((item) => (
                      <div key={item.id} className="p-4 border rounded-xl flex justify-between items-start">
                        <div className="flex gap-4">
                          {item.image_url && (
                            <img src={item.image_url} alt="" className="w-20 h-20 object-cover rounded-lg" />
                          )}
                          <div>
                            <span className={`inline-block px-2 py-0.5 text-xs rounded mb-1 ${
                              item.category === 'financial' ? 'bg-green-100 text-green-700' :
                              item.category === 'milestone' ? 'bg-blue-100 text-blue-700' :
                              item.category === 'meeting' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{item.category}</span>
                            <h4 className="font-semibold">{item.title}</h4>
                            <p className="text-sm text-gray-600 line-clamp-2">{item.content}</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteNews(item.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    {news.length === 0 && <p className="text-center py-8 text-gray-500">No news posted yet</p>}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Dividends Tab */}
            <TabsContent value="dividends">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Dividend Announcements</CardTitle>
                  <Button onClick={() => setShowDividendModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add Dividend
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-3">Amount/Share</th>
                          <th className="py-3">Record Date</th>
                          <th className="py-3">Payment Date</th>
                          <th className="py-3">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dividends.map((div) => (
                          <tr key={div.id} className="border-b">
                            <td className="py-3 font-semibold">₹{div.amount_per_share}</td>
                            <td className="py-3">{div.record_date}</td>
                            <td className="py-3">{div.payment_date}</td>
                            <td className="py-3 text-gray-600">{div.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {dividends.length === 0 && <p className="text-center py-8 text-gray-500">No dividends announced yet</p>}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Documents</CardTitle>
                  <Button onClick={() => setShowDocumentModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Add Document
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {documents.map((doc) => (
                      <div key={doc.id} className="p-4 border rounded-xl">
                        <div className="flex items-start gap-3">
                          <FileText className="w-8 h-8 text-blue-600" />
                          <div className="flex-1">
                            <h4 className="font-semibold">{doc.title}</h4>
                            <p className="text-sm text-gray-500">{doc.document_type}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {documents.length === 0 && (
                      <p className="col-span-3 text-center py-8 text-gray-500">No documents uploaded yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests">
              <Card>
                <CardHeader>
                  <CardTitle>Investment Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {requests.map((req) => (
                      <div key={req.id} className="p-4 border rounded-xl flex justify-between items-center">
                        <div>
                          <p className="font-semibold">{req.investor_name}</p>
                          <p className="text-sm text-gray-600">{req.shares_requested} shares requested</p>
                          <p className="text-xs text-gray-400">{req.message || 'No message'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {req.status === 'pending' ? (
                            <>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleRequestAction(req.id, 'approved')}>
                                <CheckCircle className="w-4 h-4 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRequestAction(req.id, 'rejected')}>
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </>
                          ) : (
                            <span className={`px-3 py-1 rounded-full text-sm ${
                              req.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>{req.status}</span>
                          )}
                        </div>
                      </div>
                    ))}
                    {requests.length === 0 && <p className="text-center py-8 text-gray-500">No investment requests</p>}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Add/Edit Investor Modal */}
      <Dialog open={showInvestorModal} onOpenChange={setShowInvestorModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingInvestor ? 'Edit Investor' : 'Add New Investor'}</DialogTitle>
            <DialogDescription>Enter investor details and credentials</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateInvestor} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {!editingInvestor && (
                <>
                  <div>
                    <Label>User ID *</Label>
                    <Input value={investorForm.user_id} onChange={(e) => setInvestorForm({...investorForm, user_id: e.target.value})} required />
                  </div>
                  <div>
                    <Label>Password *</Label>
                    <Input type="password" value={investorForm.password} onChange={(e) => setInvestorForm({...investorForm, password: e.target.value})} required />
                  </div>
                </>
              )}
              <div>
                <Label>Name *</Label>
                <Input value={investorForm.name} onChange={(e) => setInvestorForm({...investorForm, name: e.target.value})} required />
              </div>
              <div>
                <Label>City *</Label>
                <Input value={investorForm.city} onChange={(e) => setInvestorForm({...investorForm, city: e.target.value})} required />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={investorForm.email} onChange={(e) => setInvestorForm({...investorForm, email: e.target.value})} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={investorForm.phone} onChange={(e) => setInvestorForm({...investorForm, phone: e.target.value})} />
              </div>
              <div>
                <Label>Shares Held *</Label>
                <Input type="number" value={investorForm.shares_held} onChange={(e) => setInvestorForm({...investorForm, shares_held: e.target.value})} required />
              </div>
              <div>
                <Label>Amount Invested (₹) *</Label>
                <Input type="number" value={investorForm.amount_invested} onChange={(e) => setInvestorForm({...investorForm, amount_invested: e.target.value})} required />
              </div>
              <div>
                <Label>Entry Share Value (₹) *</Label>
                <Input type="number" value={investorForm.entry_share_value} onChange={(e) => setInvestorForm({...investorForm, entry_share_value: e.target.value})} required />
              </div>
              <div>
                <Label>Discounted Share Value (₹) *</Label>
                <Input type="number" value={investorForm.discounted_share_value} onChange={(e) => setInvestorForm({...investorForm, discounted_share_value: e.target.value})} required />
              </div>
              <div className="col-span-2">
                <Label>Current Share Value (₹) *</Label>
                <Input type="number" value={investorForm.current_share_value} onChange={(e) => setInvestorForm({...investorForm, current_share_value: e.target.value})} required />
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingInvestor ? 'Update Investor' : 'Create Investor'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add News Modal */}
      <Dialog open={showNewsModal} onOpenChange={setShowNewsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add News/Update</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateNews} className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select value={newsForm.category} onValueChange={(v) => setNewsForm({...newsForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="milestone">Milestone</SelectItem>
                  <SelectItem value="meeting">Meeting Notice</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={newsForm.title} onChange={(e) => setNewsForm({...newsForm, title: e.target.value})} required />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea value={newsForm.content} onChange={(e) => setNewsForm({...newsForm, content: e.target.value})} required />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input value={newsForm.image_url} onChange={(e) => setNewsForm({...newsForm, image_url: e.target.value})} placeholder="https://..." />
            </div>
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Post News
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Dividend Modal */}
      <Dialog open={showDividendModal} onOpenChange={setShowDividendModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Dividend Announcement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDividend} className="space-y-4">
            <div>
              <Label>Amount per Share (₹) *</Label>
              <Input type="number" step="0.01" value={dividendForm.amount_per_share} onChange={(e) => setDividendForm({...dividendForm, amount_per_share: e.target.value})} required />
            </div>
            <div>
              <Label>Record Date *</Label>
              <Input type="date" value={dividendForm.record_date} onChange={(e) => setDividendForm({...dividendForm, record_date: e.target.value})} required />
            </div>
            <div>
              <Label>Payment Date *</Label>
              <Input type="date" value={dividendForm.payment_date} onChange={(e) => setDividendForm({...dividendForm, payment_date: e.target.value})} required />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={dividendForm.description} onChange={(e) => setDividendForm({...dividendForm, description: e.target.value})} />
            </div>
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Add Dividend
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Document Modal */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Document</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDocument} className="space-y-4">
            <div>
              <Label>Document Type</Label>
              <Select value={documentForm.document_type} onValueChange={(v) => setDocumentForm({...documentForm, document_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="report">Report</SelectItem>
                  <SelectItem value="certificate">Certificate</SelectItem>
                  <SelectItem value="agreement">Agreement</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={documentForm.title} onChange={(e) => setDocumentForm({...documentForm, title: e.target.value})} required />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={documentForm.description} onChange={(e) => setDocumentForm({...documentForm, description: e.target.value})} />
            </div>
            <div>
              <Label>File URL *</Label>
              <Input value={documentForm.file_url} onChange={(e) => setDocumentForm({...documentForm, file_url: e.target.value})} placeholder="https://..." required />
            </div>
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Add Document
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Update Share Price Modal */}
      <Dialog open={showPriceModal} onOpenChange={setShowPriceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Share Price</DialogTitle>
            <DialogDescription>This will update the current share value for all investors</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePrice} className="space-y-4">
            <div>
              <Label>New Share Price (₹)</Label>
              <Input type="number" step="0.01" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} required />
            </div>
            <Button type="submit" className="w-full" disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Update Price
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
